import { useState } from 'react';

import HashTag from 'models/hashtag';
import { useDispatch } from 'react-redux';

import { getOrganizationTags, saveOrganizationTagSubObjective, updateOrganizationSubObjective } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

const useTableState = ({ parentTag = null, teamId = null, tagType = 'SUB_OBJECTIVE_TAG', enableParentExpansion }) => {
  const dispatch = useDispatch();
  const [tags, setTags] = useState([]);

  const [loading, setLoading] = useState(false);

  const [newTag, setNewTag] = useState(new HashTag());
  const [showTagCreationRow, setShowAddTagCreationRow] = useState(false);

  // getting data
  const getAllTags = (successCallback = () => {}) => {
    // make api call and save the data inside an array.
    setLoading(true);
    getOrganizationTags({
      teamId: -1,
      tagType: 'SUB_OBJECTIVE_TAG',
      orgTagId: parentTag.id,
    })
      .then((tags) => {
        const activeTags = tags.filter((tag) => tag.active);
        setTags(
          activeTags.map((tag) => ({
            ...tag,
            isEditing: false,
            hasChildTags: true,
          }))
        );
        // successCallback(activeTags);
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message)))
      .finally(() => setLoading(false));
  };

  // updating rowData
  const updateTag = (tag) => {
    setLoading(true);
    updateOrganizationSubObjective({
      orgTag: { ...tag, displayName: tag.name },
      teamId,
      tagType,
      parentTagId: parentTag.id,
    })
      .then((orgTags) => {
        getAllTags();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => setLoading(false));
  };

  const createNewTag = (tag) => {
    setLoading(true);
    saveOrganizationTagSubObjective({
      orgTag: { ...tag, displayName: tag.name },
      teamId,
      tagType,
      parentTagId: parentTag.id,
    })
      .then((orgTags) => {
        getAllTags();
        hideNewTagCreationRow();
        enableParentExpansion();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => setLoading(false));
  };

  // this will enable edit mode for given tagId
  // and disable tag editing for rest tags
  const enableTagEditing = (tagId) => {
    hideNewTagCreationRow();
    setTags(
      tags.map((tag) => ({
        ...tag,
        isEditing: tag.id === tagId,
      }))
    );
  };

  const disableEditingForAllTags = (tagId) => {
    setTags(
      tags.map((tag) => ({
        ...tag,
        isEditing: false,
      }))
    );
  };

  const toggleTagEditing = (tagId) => {
    hideNewTagCreationRow();
    setTags(
      tags.map((tag) => {
        if (tag.id === tagId) {
          tag.isEditing = !tag.isEditing;
        } else {
          tag.isEditing = false;
        }
        return tag;
      })
    );
  };

  const handleToggleActive = ({ target }, tag) => {
    // const isActive = target.checked;
    const isActive = false;
    updateTag({ ...tag, active: isActive });
  };

  // creating data
  const displayNewTagCreationRow = () => {
    setShowAddTagCreationRow(true);
    disableEditingForAllTags();
  };

  const hideNewTagCreationRow = () => {
    setShowAddTagCreationRow(false);
    setNewTag(new HashTag());
  };

  const disableExpansion = (tagId) => {
    setTags(
      tags.map((tag) => {
        if (tagId === tag.id) tag.hasChildTags = false;
        return tag;
      })
    );
  };

  const enableExpansion = (tagId) => {
    setTags(
      tags.map((tag) => {
        if (tagId === tag.id) tag.hasChildTags = true;
        return tag;
      })
    );
  };

  return {
    tags,
    loading,
    newTag,
    showTagCreationRow,
    getAllTags,
    updateTag,
    createNewTag,
    enableTagEditing,
    disableEditingForAllTags,
    handleToggleActive,
    displayNewTagCreationRow,
    hideNewTagCreationRow,
    toggleTagEditing,
    disableExpansion,
    enableExpansion,
  };
};

export default useTableState;
